import { FormEvent, useState } from "react";
import SiteLayout from "@/components/site/SiteLayout";
import PageHero from "@/components/site/PageHero";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { useI18n } from "@/lib/i18n";

export default function Kontakt() {
  const { t } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    imie: "",
    klinika: "",
    telefon: "",
    email: "",
    zespol: "",
    wiadomosc: "",
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.imie || !form.klinika || !form.email) {
      toast({
        title: t.contact.missingTitle,
        description: t.contact.missingDescription,
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      toast({
        title: t.contact.thanksTitle,
        description: t.contact.thanksDescription,
      });
      setForm({ imie: "", klinika: "", telefon: "", email: "", zespol: "", wiadomosc: "" });
      setSubmitting(false);
    }, 600);
  };

  return (
    <SiteLayout>
      <PageHero eyebrow={t.contact.eyebrow} title={t.contact.title} description={t.contact.description} />

      <section className="py-12 lg:py-16 border-b border-border">
        <div className="mx-auto max-w-container px-6 lg:px-10 grid lg:grid-cols-[1fr_1.4fr] gap-12">
          <aside className="space-y-8 reveal">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">{t.contact.direct}</div>
              <a href="mailto:kontakt@medessa.pl" className="font-serif text-2xl text-foreground hover:text-muted-foreground transition-colors">
                kontakt@medessa.pl
              </a>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">{t.contact.phone}</div>
              <div className="font-serif text-2xl text-foreground">+48 600 000 000</div>
              <div className="text-sm text-muted-foreground mt-2">{t.contact.hours}</div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">{t.contact.office}</div>
              <div className="text-foreground">Medessa sp. z o.o.</div>
              <div className="text-muted-foreground text-sm mt-1">{t.contact.location}</div>
            </div>

            <div className="border border-border rounded-lg p-0 overflow-hidden bg-card">
              <div className="p-4 border-b border-border bg-muted/50">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.contact.calendly}</div>
              </div>
              <div className="calendly-inline-widget" data-url="https://calendly.com/wg-agm-investments/30min" style={{ minWidth: "320px", height: "500px" }} />
            </div>
            <script type="text/javascript" src="https://assets.calendly.com/assets/external/widget.js" async />
          </aside>

          <form onSubmit={onSubmit} className="reveal space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="imie" className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-normal">{t.contact.name}</Label>
                <Input id="imie" value={form.imie} onChange={(e) => setForm({ ...form, imie: e.target.value })} className="h-12 bg-background border-border rounded-md" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="klinika" className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-normal">{t.contact.clinic}</Label>
                <Input id="klinika" value={form.klinika} onChange={(e) => setForm({ ...form, klinika: e.target.value })} className="h-12 bg-background border-border rounded-md" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-normal">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 bg-background border-border rounded-md" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefon" className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-normal">{t.contact.phone}</Label>
                <Input id="telefon" type="tel" value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} className="h-12 bg-background border-border rounded-md" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-normal">{t.contact.employees}</Label>
              <Select value={form.zespol} onValueChange={(v) => setForm({ ...form, zespol: v })}>
                <SelectTrigger className="h-12 bg-background border-border rounded-md">
                  <SelectValue placeholder={t.contact.employeesPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="<10">{t.contact.teamOptions[0]}</SelectItem>
                  <SelectItem value="10-25">{t.contact.teamOptions[1]}</SelectItem>
                  <SelectItem value="25-50">{t.contact.teamOptions[2]}</SelectItem>
                  <SelectItem value="50+">{t.contact.teamOptions[3]}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wiadomosc" className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-normal">{t.contact.message}</Label>
              <Textarea id="wiadomosc" rows={5} value={form.wiadomosc} onChange={(e) => setForm({ ...form, wiadomosc: e.target.value })} className="bg-background border-border rounded-md resize-none" placeholder={t.contact.placeholder} />
            </div>

            <div className="pt-4 flex flex-col sm:flex-row sm:items-center gap-4">
              <button type="submit" disabled={submitting} className="inline-flex items-center justify-center rounded-md bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-60">
                {submitting ? t.contact.sending : t.contact.submit}
              </button>
              <p className="text-xs text-muted-foreground">{t.contact.response}</p>
            </div>
          </form>
        </div>
      </section>
    </SiteLayout>
  );
}
